import { useState, useEffect } from 'react';
import {
  HistoryOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Skeleton, Timeline, Empty, Tag, Select, Button, Space } from 'antd';
import moment from 'moment';

import {
  useGetLogActionsByStopoverQuery,
  LogActionsQueryParams,
} from '../../../services/logActionsApi';
import { Modal } from '../../antd/Modal';
import { ModalLogActionsWrapper } from './styles';

type ModalLogActionsProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  stopoverId: number | undefined;
  vesselName: string;
};

const LOG_ACTION_LABELS: Record<string, string> = {
  // Escalas
  create_stopover: 'Criação de escala',
  update_stopover: 'Atualização de escala',
  cancel_stopover: 'Cancelamento de escala',
  reactivate_stopover: 'Reativação de escala',
  reopen_stopover: 'Reabertura de escala',

  // Atracações
  update_docking: 'Atualização de atracação',
  update_docking_place: 'Atualização de berço',
};

function getActionLabel(action: string): string {
  return LOG_ACTION_LABELS[action] || action;
}

// Cores para os badges baseadas no tipo de ação
function getActionColor(action: string): string {
  if (action.includes('create')) return 'green';
  if (action.includes('cancel')) return 'red';
  if (action.includes('reactivate') || action.includes('reopen')) return 'blue';
  if (action.includes('confirm')) return 'cyan';
  if (action.includes('update')) return 'orange';
  return 'default';
}

export function ModalLogActions(props: ModalLogActionsProps) {
  const { showModal, setShowModal, stopoverId, vesselName } = props;

  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const queryParams: LogActionsQueryParams = {
    stopoverId: stopoverId || 0,
    page,
    pageSize: 15,
    userId: selectedUserId,
  };

  const { data, isFetching } = useGetLogActionsByStopoverQuery(queryParams, {
    skip: !stopoverId || !showModal,
  });

  useEffect(() => {
    setPage(1);
  }, [selectedUserId]);

  useEffect(() => {
    if (!showModal) {
      setPage(1);
      setSelectedUserId(null);
    }
  }, [showModal]);

  function formatUserName(
    user: { first_name?: string; last_name?: string; email?: string } | null
  ): string {
    if (!user) return 'Usuário não identificado';
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email || 'Usuário não identificado';
  }

  const logActions = data?.results || [];
  const pagination = data?.pagination;
  const availableUsers = data?.filters?.available_users || [];

  return (
    <Modal
      visible={showModal}
      className="TOSPrimaryModal"
      width={600}
      title={
        <>
          <HistoryOutlined /> Histórico de Ações - {vesselName}
        </>
      }
      onCancel={() => setShowModal(false)}
      footer={null}
    >
      <ModalLogActionsWrapper>
        {/* Filtros */}
        <div className="filters-container">
          <Space>
            <FilterOutlined />
            <Select
              placeholder="Filtrar por usuário"
              allowClear
              style={{ width: 200 }}
              value={selectedUserId}
              onChange={(value) => setSelectedUserId(value || null)}
              loading={isFetching}
              size="small"
            >
              {availableUsers.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name}
                </Select.Option>
              ))}
            </Select>
          </Space>
        </div>

        {/* Contador */}
        {pagination && (
          <div className="log-count">
            {pagination.total_count}{' '}
            {pagination.total_count === 1
              ? 'registro encontrado'
              : 'registros encontrados'}
          </div>
        )}

        <Skeleton loading={isFetching} active>
          {logActions.length > 0 ? (
            <>
              <div className="timeline-container">
                <Timeline mode="left">
                  {logActions.map((log) => (
                    <Timeline.Item
                      key={log.id}
                      color={getActionColor(log.action)}
                      label={
                        <div className="timeline-label">
                          <ClockCircleOutlined />{' '}
                          {moment(log.log_date).format('DD/MM/YYYY HH:mm:ss')}
                        </div>
                      }
                    >
                      <div className="timeline-content">
                        <Tag color={getActionColor(log.action)}>
                          {getActionLabel(log.action)}
                        </Tag>
                        <div className="user-info">
                          <UserOutlined /> {formatUserName(log.user)}
                        </div>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>

              {/* Paginação */}
              {pagination && pagination.total_pages > 1 && (
                <div className="pagination-container">
                  <Button
                    size="small"
                    type="text"
                    icon={<LeftOutlined />}
                    disabled={!pagination.has_previous}
                    onClick={() => setPage((p) => p - 1)}
                  />
                  <span className="pagination-info">
                    Página {pagination.page} de {pagination.total_pages}
                  </span>
                  <Button
                    size="small"
                    type="text"
                    icon={<RightOutlined />}
                    disabled={!pagination.has_next}
                    onClick={() => setPage((p) => p + 1)}
                  />
                </div>
              )}
            </>
          ) : (
            <Empty
              description="Nenhum registro de ação encontrado"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Skeleton>
      </ModalLogActionsWrapper>
    </Modal>
  );
}
